import React, { useEffect, useState } from 'react';
import { Grid, Box, Typography, Stack, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CardTitle from './CardTitle';
import WidgetCard from './WidgetCard';
import CenteredSpinner from '../../../components/CenteredSpinner';
import DashboardServices from '../../../services/DashboardServices';
import localStorageService from '../../../services/localStorageService';

const StatusCard = ({ title, value, color }) => (
    <WidgetCard >
        <Stack spacing={1}>
            <CardTitle>{title}</CardTitle>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 700,
                    color: color,
                }}
            >
                {String(value).padStart(2, '0')}
            </Typography>
        </Stack>
    </WidgetCard>
);

const TotalCasesCard = ({ totalCases, mom, last30Days, color }) => (
    <WidgetCard sx={{ minHeight: 200 }}>
        <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <CardTitle>Total Cases</CardTitle>
                {mom && (
                    <Box
                        sx={{
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                            px: 1.5,
                            py: 0.5
                        }}
                    >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            +{mom}%
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: 10 }}>
                            MoM
                        </Typography>
                    </Box>
                )}
            </Stack>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 700,
                    color: color,
                }}
            >
                {totalCases?.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Since launch
            </Typography>
            {/* <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box 
                        sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: color 
                        }} 
                    />
                    <Typography variant="body2" color="text.secondary">
                        Last 30 days: {last30Days?.toLocaleString()}
                    </Typography>
                </Stack>
            </Box> */}
        </Stack>
    </WidgetCard>
);

const OngoingVsResolvedCard = ({ ongoing, resolved, resolvedPercentage, color }) => {
    const ongoingColor = '#5e87d4';
    const resolvedColor = '#7cb342';

    return (
        <WidgetCard sx={{ minHeight: 200 }}>
            <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <CardTitle>Ongoing vs Resolved</CardTitle>
                    {resolvedPercentage !== undefined && (
                        <Box
                            sx={{
                                bgcolor: '#f5f5f5',
                                borderRadius: 1,
                                px: 1.5,
                                py: 0.5
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#333333' }}>
                                {resolvedPercentage}%
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontSize: 10, color: '#333333' }}>
                                Resolved
                            </Typography>
                        </Box>
                    )}
                </Stack>
                <Stack className='mt--2' direction="row" spacing={1} alignItems="baseline">
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            color: ongoingColor,
                        }}
                    >
                        {ongoing?.toLocaleString()}
                    </Typography>
                    <Typography variant="h4" sx={{ color: 'text.secondary' }}>/</Typography>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            color: resolvedColor
                        }}
                    >
                        {resolved?.toLocaleString()}
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    Ongoing / Resolved
                </Typography>
                <Box sx={{ borderTop: '1px solid #e0e0e0', pt: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                        <Box sx={{ flex: ongoing, height: 6, bgcolor: ongoingColor, borderRadius: 1 }} />
                        <Box sx={{ flex: resolved, height: 6, bgcolor: resolvedColor, borderRadius: 1 }} />
                    </Stack>
                </Box>
            </Stack>
        </WidgetCard>
    );
};

const CaseStatusSummary = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [statusData, setStatusData] = useState({
        ongoing_cases: 0,
        resolved_cases: 0,
        rejected_cases: 0,
        total_cases: 0,
        last_30_days: 0,
        mom_percentage: null,
        resolved_percentage: null
    });

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                // Get params with organization_id if applicable
                const storedUser = localStorageService?.getItem?.('user') ?? null;
                const roles = (storedUser?.roles) || (storedUser?.role) || storedUser?.rolesArray || [];
                const normalizedRoles = Array.isArray(roles) ? roles.map(r => String(r).toLowerCase()) : [];
                const targetRoles = ['organization admin', 'case officer', 'case manager', 'organization head', 'director cid', 'director ncpa', "deputy director",'oic','department head','team leader first respondent','call center officer mowca', 'counselling officer', 'legal officer'];
                const hasTargetRole = normalizedRoles.some(r => targetRoles.includes(r));

                const params = {};

                if (hasTargetRole) {
                    const orgId = storedUser?.employee?.organization_id ?? storedUser?.organization_id ?? storedUser?.org?.id ?? null;
                    if (orgId) params.organization_id = orgId;
                }

                const targetEmployee_id_Roles = ['case officer', 'first respondent', 'counselling officer', 'legal officer'];
                const needToPassUserId = normalizedRoles.some(r => targetEmployee_id_Roles.includes(r));
                if (needToPassUserId) {
                    const employeeId = storedUser?.employee?.id ?? storedUser?.employee_id ?? null;
                    if (employeeId) params.employee_id = employeeId;
                }

                const res = await DashboardServices.getDashboardCaseAssignmentStatusSummary(params);
                const payload = res?.data ?? res;
                // Expected: { view: { msg, data: { ongoing_cases, resolved_cases, rejected_cases } } }
                const data = payload?.view?.data ?? payload?.data ?? {};

                if (mounted) {
                    const ongoing = Number(data.ongoing_assignments) || 0;
                    const resolved = Number(data.resolved_assignments) || 0;
                    const rejected = Number(data.rejected_assignments) || 0;

                    // Calculate total cases
                    const total = ongoing + resolved + rejected;

                    // Calculate resolved percentage
                    const resolvedPct = total > 0 ? Math.round((resolved / total) * 100) : 0;

                    setStatusData({
                        ongoing_cases: ongoing,
                        resolved_cases: resolved,
                        rejected_cases: rejected,
                        total_cases: total,
                        last_30_days: Number(data.last_30_days) || 0,
                        mom_percentage: data.mom_percentage != null ? Number(data.mom_percentage) : null,
                        resolved_percentage: resolvedPct
                    });
                }
            } catch (err) {
                console.error('Failed to load case status summary', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => { mounted = false; };
    }, []);

    //if (loading) return <CenteredSpinner />;

    return (
        <Grid container spacing={2}>
            <Grid size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
                <StatusCard
                    title="Ongoing Cases"
                    value={statusData.ongoing_cases}
                    color={theme.palette.primary.main}
                />
            </Grid>
            <Grid size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
                <StatusCard
                    title="Resolved Cases"
                    value={statusData.resolved_cases}
                    color={theme.palette.primary.main}
                />
            </Grid>
            <Grid size={{ lg: 4, md: 4, sm: 12, xs: 12 }}>
                <StatusCard
                    title="Rejected Cases"
                    value={statusData.rejected_cases}
                    color={theme.palette.primary.main}
                />
            </Grid>
            <Grid size={{ lg: 5, md: 5, sm: 12, xs: 12 }}>
                <TotalCasesCard
                    totalCases={statusData.total_cases}
                    mom={statusData.mom_percentage}
                    last30Days={statusData.last_30_days}
                    color={theme.palette.primary.main}
                />
            </Grid>
            <Grid size={{ lg: 7, md: 7, sm: 12, xs: 12 }}>
                <OngoingVsResolvedCard
                    ongoing={statusData.ongoing_cases}
                    resolved={statusData.resolved_cases}
                    resolvedPercentage={statusData.resolved_percentage}
                    color={theme.palette.primary.main}
                />
            </Grid>
        </Grid>
    );
};

export default CaseStatusSummary;
